import { create } from 'zustand';

import { defaultColors } from '~/core/colors';
import { Domain } from '~/types/axis.types';
import {
  BaseChartAPIProps,
  ChartAPIFunctionProps,
  CreateChartOptions,
  CreateChartOptionsWithColors,
} from '~/types/create.types';
import { SeriesType } from '~/types/series.types';
import { TimeSeriesItem, TsDataPoint } from '~/types/timeseries.types';
import { RangeHorizon } from '~/ui/components/resample-selector/ResampleSelector.types';
import { extendData, getDomainFromHorizon } from '~/utils/time-series';
import { calculateYAxisWidth } from '~/utils/y-scale/y-scale';

import { recalculateScale } from '../recalculate/recalculate';

interface InitialiseProps {
  horizon: RangeHorizon;
  data: TsDataPoint[];
  timeseries: TimeSeriesItem[];
  options: CreateChartOptions;
  context: CanvasRenderingContext2D;
}
export interface BaseReturn {
  base?: BaseChartAPIProps;
  setBase: (update: Partial<BaseChartAPIProps>) => void;
  initialise: (props: InitialiseProps) => ChartAPIFunctionProps;
  getBase: () => BaseChartAPIProps | undefined;
}

type ChartStore = {
  base?: BaseChartAPIProps;
  initialise: (props: InitialiseProps) => BaseChartAPIProps;
  updateDomain: (domain: Domain) => BaseChartAPIProps;
  updateSeriesType: (seriesType: SeriesType) => BaseChartAPIProps;
};

const calculateBase = ({
  horizon,
  data,
  timeseries,
  context,
  options,
}: InitialiseProps): BaseChartAPIProps => {
  const optionsWithColors: CreateChartOptionsWithColors = {
    config: {
      hideXAxis: false,
      yAxisPosition: 'right',
      ...options.config,
    },
    colors: { ...defaultColors, ...options.colors },
    gutters: {
      x: options.gutters.x,
      y: calculateYAxisWidth(context, data),
    },
    height: options.height,
    width: options.width,
  };

  const extendedData = extendData(data);
  const domain = getDomainFromHorizon(extendedData, horizon, data.length - 1);
  const roundedDomain: Domain = [Math.round(domain[0]), Math.round(domain[1])];

  const initialProps = {
    options: optionsWithColors,
    domain: roundedDomain,
    data: extendedData,
  };

  const scale = recalculateScale(initialProps);

  const baseProps: BaseChartAPIProps = {
    ...initialProps,
    context,
    timeseries,
    x: scale.x,
    y: scale.y,
    lastDataIdx: data.length - 1,
    seriesType: 'line',
  };
  return baseProps;
};

export const chartStore = create<ChartStore>((set, get) => ({
  base: undefined,
  initialise: ({
    horizon,
    data,
    timeseries,
    options,
    context,
  }: InitialiseProps) => {
    const base = calculateBase({
      horizon,
      data,
      timeseries,
      context,
      options,
    });

    set({ base });
    return base;
  },
  updateDomain: (domain: Domain) => {
    const base = get().base as BaseChartAPIProps;
    const roundedDomain: Domain = [
      Math.round(domain[0]),
      Math.round(domain[1]),
    ];
    const initialProps = {
      options: base.options,
      domain: roundedDomain,
      data: base.data,
    };

    const scale = recalculateScale(initialProps);
    const newBase = { ...base, domain, x: scale.x, y: scale.y };

    set({ base: newBase });
    return newBase;
  },
  updateSeriesType: (seriesType: SeriesType) => {
    const base = get().base as BaseChartAPIProps;
    const newBase = { ...base, seriesType };
    set({ base: newBase });
    return newBase;
  },
}));
